import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import useDebounce from "../../hooks/useDebounce";
import AppTextField from "../../lib/components/AppTextField";
import AppDateField from "../../lib/components/AppDateField";
import {
  Button,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Add, Edit, Search } from "@mui/icons-material";
import scrollHandler from "../../utils/scrollHandler";
import AppSelectField from "../../lib/components/AppSelectField";
import pathnames from "../../constants/pathnames";
import EmptyState from "../../lib/components/EmptyState";
import integrationService from "../../service/integrationService";
import { useMutation, useQuery } from "@tanstack/react-query";
import AddNewIntegration from "./add-integration/AddNewIntegration";
import PreloaderTable from "../../lib/components/PreloaderTable";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import _ from "lodash";

const Integrations = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const dateFrom = searchParams.get("dateFrom") || null;
  const dateTo = searchParams.get("dateTo") || null;
  const status = searchParams.get("status") || "";
  const searchTerm = useDebounce(keyword);
  const [isAddNew, setIsAddNew] = React.useState(false);
  const [integration, setIntegration] = React.useState(null);

  const queries = {};
  if (page) queries.page = page;
  if (searchTerm) queries.keyword = searchTerm;
  if (status) queries.status = status;
  if (dateTo) queries.dateTo = dateTo;
  if (dateFrom) queries.dateFrom = dateFrom;
  if (limit) queries.limit = limit;

  const query = useQuery({
    retry: false,
    queryKey: ["integrations", ...Object.values(queries)],
    queryFn: () => integrationService.get({ ...queries }),
  });

  const mutation = useMutation({
    mutationFn: (data) => integrationService.activate(data, { id: data?.id, organizationId: data?.organizationId }),
    onSuccess: (data) => {
      query.refetch();
      toast.success("Connection updated successfully...");
    },
    onError: ({ response }) => errorHandler(response),
  });

  function handleChange(e, name = "") {
    const value = name.includes("date") ? moment(e).format("YYYY-MM-DD") : name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 }
    );
    if (name === "page") scrollHandler();
  }

  const { isPending: isLoading, data } = query;
  const integrationsData = data?.data;
  return (
    <AppLayout>
      <Header title="Integrations" description="Get insights to accounts on jureb here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className={`row mb-2`}>
          <div className="col-6 col-md-3 col-lg-4 mb-3">
            <div>
              <AppTextField
                placeholder="Search integrations..."
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField label="Date From" value={dateFrom} onChange={(e) => handleChange(e, "dateFrom")} />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField label="Date To" value={dateTo} minDate={dateFrom} onChange={(e) => handleChange(e, "dateTo")} />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppSelectField
                value={status}
                placeholder="Status"
                onChange={(e) => handleChange(e, "status")}
                items={[
                  { label: "All", value: "ALL" },
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                  { label: "Onboarding", value: "ONBOARDING" },
                ]}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div className="d-flex justify-content-end">
              <Button onClick={() => setIsAddNew(true)} endIcon={<Add />} variant="contained" color="secondary">
                Add New
              </Button>
            </div>
          </div>
        </div>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead className="bg-light">
                <TableRow>
                  <TableCell>Business Name</TableCell>
                  <TableCell>Date Integrated</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>No. of Transmissions</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrationsData?.data?.map((item) => (
                  <TableRow key={item?.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell className="">
                      <Link to={`${pathnames.ORGANIZATIONS}/${item?.organizationId}`} className="fw-medium">
                        {item?.organizationMeta?.name}
                      </Link>
                    </TableCell>
                    <TableCell className="">{moment(item?.createdAt).format("Do MMM, YYYY")}</TableCell>
                    <TableCell className="">
                      <Status status={item?.status} />
                    </TableCell>
                    <TableCell className="">{item?.inbox?.messageCount}</TableCell>
                    <TableCell className="">
                      <span className="d-flex align-items-center gap-3 justify-content-center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setIsAddNew(true);
                            setIntegration(item);
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <Switch onClick={() => mutation.mutate(item)} checked={item?.status === "ACTIVE"} color="secondary" />
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <EmptyState isEmpty={!isLoading && !integrationsData?.data?.length} />
        </Paper>
        <PreloaderTable isLoading={isLoading} />

        <div className="my-3">
          <div className="d-flex align-items-center gap-4 justify-content-between">
            <div>
              <AppSelectField
                value={limit}
                placeholder="Limit"
                onChange={(e) => handleChange(e, "limit")}
                items={[
                  { label: "20", value: "20" },
                  { label: "30", value: "30" },
                  { label: "40", value: "40" },
                  { label: "50", value: "50" },
                ]}
              />
            </div>
            <div>
              <Pagination
                page={Number(page)}
                count={integrationsData?.totalPages}
                onChange={(_, value) => {
                  handleChange(value, "page");
                }}
                shape="rounded"
                color="primary"
              />
            </div>
          </div>
        </div>
      </div>
      <AddNewIntegration
        integrationData={integration}
        isOpen={isAddNew}
        refetch={() => query.refetch()}
        onClose={() => {
          setIntegration(null);
          setIsAddNew(false);
        }}
      />
    </AppLayout>
  );
};

function Status({ status }) {
  if (status === "ACTIVE") return <div className="badge rounded-pill  fw-medium fs-sm bg-primary text-primary bg-opacity-25">Active</div>;
  if (status === "INACTIVE") return <div className="badge bg-danger text-danger rounded-pill fw-medium fs-sm bg-opacity-25">Inactive</div>;
  if (status === "ONBOARDING")
    return <div className="badge bg-warning text-warning rounded-pill fw-medium fs-sm bg-opacity-25">Onboarding</div>;
  return null;
}

export default Integrations;
